import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLanguages, deleteLanguage } from "@/api/languages";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowUpDown, Edit, Globe, Plus, Search, Trash } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Language } from "@/types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Get color for language badge
const getLanguageBadgeColor = (code: string): string => {
  // Define a set of vibrant transparent background and text color pairs
  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  // Use a deterministic way to choose a color based on language code
  const codeNum = code
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = codeNum % colorPairs.length;

  return colorPairs[index];
};

const LanguagesList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"code" | "name">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [languageToDelete, setLanguageToDelete] = useState<Language | null>(
    null
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: languages = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  const handleSort = (column: "code" | "name") => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  // Handle delete click
  const handleDeleteClick = (language: Language) => {
    setLanguageToDelete(language);
    setConfirmDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!languageToDelete) return;

    try {
      await deleteLanguage(languageToDelete.code);

      // Invalidate query to refetch the languages
      queryClient.invalidateQueries({ queryKey: ["languages"] });

      toast({
        title: "Language deleted",
        description: `"${languageToDelete.name}" has been removed.`,
      });
    } catch (error) {
      console.error("Error deleting language:", error);

      let errorMessage = "Failed to delete the language.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLanguageToDelete(null);
      setConfirmDialogOpen(false);
    }
  };

  // Filter languages based on search query
  const filteredLanguages = languages.filter(
    (language) =>
      language.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      language.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort languages
  const sortedLanguages = [...filteredLanguages].sort((a, b) => {
    if (sortBy === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else {
      return sortDirection === "asc"
        ? a.code.localeCompare(b.code)
        : b.code.localeCompare(a.code);
    }
  });

  const addLanguageButton = (
    <Button onClick={() => navigate("/admin/system/languages/new")}>
      <Plus className="mr-2 h-4 w-4" />
      Add Language
    </Button>
  );

  return (
    <AdminLayout title="Languages" actionButton={addLanguageButton}>
      <div className="flex justify-between items-center mb-6">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search languages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search languages..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead
                  className="w-[150px] cursor-pointer"
                  onClick={() => handleSort("code")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Language Code</span>
                    {sortBy === "code" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Language Name</span>
                    {sortBy === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-24 text-center text-destructive"
                  >
                    Error loading languages
                  </TableCell>
                </TableRow>
              ) : sortedLanguages.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-24 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Globe className="h-8 w-8 mb-2 text-muted-foreground/50" />
                      <h3 className="font-medium">No languages found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery
                          ? `No languages match "${searchQuery}"`
                          : "There are no languages defined yet."}
                      </p>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          onClick={() => setSearchQuery("")}
                          className="mt-2"
                          size="sm"
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                sortedLanguages.map((language) => (
                  <TableRow key={language.code} className="hover:bg-muted/50">
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-mono uppercase font-medium px-2 py-1 border-transparent",
                          getLanguageBadgeColor(language.code)
                        )}
                      >
                        {language.code}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {language.name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={language.is_active ? "default" : "outline"}
                      >
                        {language.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            navigate(
                              `/admin/system/languages/edit/${language.code}`
                            )
                          }
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteClick(language)}
                        >
                          <Trash className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Language</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium">{languageToDelete?.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default LanguagesList;
